/**
 * localStorage와 동기화되는 상태 관리 Hook입니다.
 *
 * 주요 기능:
 * - localStorage와 React 상태 자동 동기화
 * - 키 변경 시 올바른 값 반환
 * - JSON 직렬화/역직렬화 자동 처리
 *
 * @remarks
 * 브라우저 새로고침 후에도 유지되어야 하는 설정값에 사용합니다.
 * 예: 테마 설정, 차트 너비, 사용자 환경설정
 *
 * @example
 * ```tsx
 * const [theme, setTheme] = useLocalStorage('theme', 'light');
 * ```
 */
import { useCallback, useDebugValue, useEffect, useRef, useState } from 'react';
import isNil from 'lodash-es/isNil';

/**
 * localStorage와 동기화되는 상태를 관리합니다.
 *
 * @typeParam S - 저장할 값의 타입
 * @param key - localStorage 키
 * @param initialState - 초기값 또는 초기값 생성 함수
 * @returns [저장된 값, 값 설정 함수] 튜플
 */
export const useLocalStorage = <S>(
  key: string,
  initialState?: S | ((item: string | null) => S),
): [S, React.Dispatch<React.SetStateAction<S>>] => {
  // useRef로 직접 관리: ESLint는 ref 객체의 .current 접근을 deps 불필요 항목으로 인식한다.
  // usePrevious(key)는 동일하게 useRef 기반이지만, 반환된 값은 일반 변수라서
  // exhaustive-deps가 deps 포함을 요구하는 문제가 있다.
  const prevKeyRef = useRef<string | undefined>(undefined);

  const getItemWithDefault = useCallback(
    (keyToRead: string) => {
      const item = localStorage.getItem(keyToRead);
      if (typeof initialState === 'function') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        return (initialState as (item: string | null) => S)(item);
      } else if (item) {
        return parse(item);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        return initialState as S;
      }
    },
    [initialState],
  );

  const [state, setState] = useState<S>(() => {
    return getItemWithDefault(key);
  });

  useDebugValue(state);

  // 키가 변경될 때 상태 업데이트 (첫 렌더 제외 - useState 초기값에서 처리됨)
  // prevKeyRef.current: ref 객체의 .current 접근은 ESLint exhaustive-deps 경고 대상이 아니다.
  useEffect(() => {
    if (prevKeyRef.current === undefined || prevKeyRef.current === key) {
      return;
    }

    const value = getItemWithDefault(key);
    setState(value);
  }, [key, getItemWithDefault]);

  // 상태가 변경될 때 localStorage 업데이트
  useEffect(() => {
    if (prevKeyRef.current !== key) {
      return;
    }
    // if (key.startsWith('chart.timeframes')) {
    //   console.log(`useLocalStorage ${key} set`, state);
    // }
    if (isNil(state)) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(state));
    }
  }, [key, state]);

  // 모든 effect 실행 후 prevKeyRef 갱신 (usePrevious와 동일한 타이밍)
  // 의도적으로 deps 없음: 매 렌더 후 실행되어야 한다.
  useEffect(() => {
    prevKeyRef.current = key;
  });

  // key 가 달라진 경우 state 대신 실제 localStorage 값을 반환
  // 이유: return [state, setState] 만 사용하면 key가 달라질 때마다 달라지기 전 key용 state가 1회 반환되는데,
  // chart.widths.XXX 키의 경우 잘못된 state를 Allotment.defaultSizes 에 적용하여 차트 너비가 초기화되는 문제가 발생함.
  // 그렇다고 대신에 return [getItemWithDefault(key), setState];를 항상 사용하면
  // state 변경에 따른 UI 갱신이 트리거되지 않게 되어, 다크모드 토글이 즉시 반영되지 않는 오류가 발생하게 됨.
  // 그래서 키 변경 시에 한해서 localStorage 값을 반환하도록 함.
  // 이 문제를 usehooks-ts 나 mantine 등으로 대체하여 해결해보려했으나 동일한 증상이 발생하여 아래 방법으로 해결함.
  if (prevKeyRef.current !== key) {
    return [getItemWithDefault(key), setState];
  }

  return [state, setState];
};

const parse = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};
